import WebSocketApi from './WebSocketApi';
import CONSTANTS from '../utils/constants';

/**
 * Allows interaction with the Balance Pull Websocket API
 */
export default class ATPWebSocketApiClient extends WebSocketApi {
    /**
     * Checks if connected and reconnects if connection is closed
     */
    ensureConnected() {
        super.ensureConnected(CONSTANTS.ENVIRONMENT_VARIABLES.WEBSOCKETS_API_URL);
    }

    /**
     * Connects if needed and resolves once the socket is open, so a caller that must know whether its message
     * was delivered can await this before sending.
     *
     * <p>Takes no arguments, like ensureConnected above: dropping parameters from an override is allowed, but
     * changing the first one's type is not — declaring (timeoutMs?: number) against the base's
     * (url: string, timeoutMs?: number) is TS2416. The base's CONNECT_TIMEOUT_MS is the only timeout needed
     * here, and accepting one would mean threading the url through every caller.
     */
    async waitUntilConnected(): Promise<boolean> {
        return super.waitUntilConnected(CONSTANTS.ENVIRONMENT_VARIABLES.WEBSOCKETS_API_URL as string);
    }

    /**
     * Starts a new balance pull request
     * @param payload The balance pull parameters
     */
    requestBalancePull(payload: any){
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.PULL_GL_BALANCE, payload);
    }

    /**
     * Starts a request to execute a CLI
     * @param payload The execute CLI request
     */
     executeCLI(payload: any) {
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_CLI, payload);
    }

    /**
     * Starts a request to pull a CDT value
     * @param payload The pull CDT Value request
     */
     pullCDTValue(payload: any) {
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.PULL_CDT_VALUE, payload);
    }

    /**
     * Starts a request to dry run a calculation
     * @param payload The dry-run request
     */
    dryRunCalculation(payload: any) {
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.DRY_RUN, payload);
    }

    /**
     * Starts a request to generate review journals
     * @param payload The generate Review Journals request
     */
    generateReviewJournals(payload: any) {
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.GENERATE_REVIEW_JOURNALS, payload);
    }

    /**
     * Starts a request to execute a worksheet
     * @param payload The execute worksheet request
     */
    executeTPAllocationWorksheet(payload: any) {
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_WORKSHEET, payload);
    }

    /**
     * Starts a request to execute a group
     * @param payload The execute group request
     */
    executeGroup(payload: any){
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_GROUP, payload);
    }

    /**
     * Starts a request to execute a bulk reversal
     * @param payload The execute bulk reversal request
     */
    executeBulkReversal(payload: any){
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_BULK_REVERSAL, payload);
    }

    /**
     * @returns true if the action reached the server, false if it was only queued.
     *
     * Await waitUntilConnected first if the answer is to be relied on. ensureConnected only starts the
     * handshake, so on a cold socket the message is queued and this returns false even though it will be
     * delivered on open — which a caller checking the value alone would report as a failed submission.
     */
    performBulkAction(payload: any): boolean {
        this.ensureConnected();
        return this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.PERFORM_BULK_ACTION, payload);
    }


    exportPeriodCharges(payload: any){
        this.ensureConnected();
        this.sendMessage(CONSTANTS.WEB_SOCKET_API_ROUTES.EXPORT_PERIOD_CHARGES, payload);
    }
}